import { CommonModule } from '@angular/common';
import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { Role } from '../../../core/models/role';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { RoleService } from '../../../core/services/role.service';
import { SharedRoleService } from '../../../core/services/shared-role.service';
import { RoleFormComponent } from './role-form/role-form.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EmPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { Permission } from '../../../core/enums/permission.enum';

@Component({
  selector: 'em-roles',
  standalone: true,
  imports: [CommonModule, TableModule, ButtonModule, ToastModule, ConfirmDialogModule, TranslatePipe,EmPermissionDirective],
  templateUrl: './roles.component.html',
  styleUrl: './roles.component.scss',
  providers: [ConfirmationService, RoleService],
})
export class RolesComponent implements OnInit {
  roles!: Role[];
  ref: DynamicDialogRef | undefined;
  translations: any = {};
  Permission = Permission;

  constructor(
    private roleService: RoleService,
    private sharedRoleService: SharedRoleService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private dialogService: DialogService,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.getAllRoles();
    this.loadTranslations();
    
    // Subscribe to shared role changes
    this.sharedRoleService.roles$.subscribe(roles => {
      this.roles = roles;
    });
  }

  loadTranslations() {
    this.translate
      .get([
        'layout.configurations.roles.title',
        'layout.configurations.roles.name',
        'layout.configurations.roles.addRole.successMessage.detail',
        'layout.configurations.roles.editRole.successMessage.detail',
        'layout.configurations.roles.addRole.successMessage.title',
        'layout.configurations.roles.editRole.successMessage.title',
        'layout.configurations.roles.description',
        'layout.configurations.roles.actions',
        'layout.configurations.roles.deleteRole.confirmationMessage',
        'layout.configurations.roles.deleteRole.successMessage.title',
        'layout.configurations.roles.deleteRole.successMessage.detail',
      ])
      .subscribe((translations) => {
        this.translations = translations;
      });
  }

  getAllRoles() {
    this.roleService.getAllRoles().subscribe({
      next: (data: Role[]) => {
        this.sharedRoleService.setRoles(data);
      },
      error: () => {
        // Use roles from shared service (which has default roles)
        const currentRoles = this.sharedRoleService.getRoles();
        if (currentRoles.length === 0) {
          // If shared service is empty, it will initialize with default roles
          // No need to do anything here
        }
      }
    });
  }

  onAdd() {
    this.ref = this.dialogService.open(RoleFormComponent, {
      header: this.translations['layout.configurations.roles.addRole.title'],
      width: '500px',
      height: 'auto',
      contentStyle: { overflow: 'auto' },
      baseZIndex: 10000,
      maximizable: false,
      breakpoints: {
        '768px': '90vw'
      }
    });

    this.ref.onClose.subscribe((role: Role) => {
      if (role) {
        // Add role to shared service
        this.sharedRoleService.addRole(role);
        this.getAllRoles();
        this.messageService.add({
          severity: 'success',
          summary: this.translations['layout.configurations.roles.addRole.successMessage.title'],
          detail: this.translations['layout.configurations.roles.addRole.successMessage.detail'],
        });
      }
    });
  }

  onEdit(role: Role) {
    const originalRole = { ...role };
    this.ref = this.dialogService.open(RoleFormComponent, {
      data: {
        role: role,
      },
      header: this.translations['layout.configurations.roles.editRole.title'],
      width: '500px',
      height: 'auto',
      contentStyle: { overflow: 'auto' },
      baseZIndex: 10000,
      maximizable: false,
      breakpoints: {
        '768px': '90vw'
      }
    });

    this.ref.onClose.subscribe((updatedRole: Role) => {
      if (updatedRole) {
      if (JSON.stringify(originalRole) === JSON.stringify(updatedRole)) {
        // No changes made
        this.messageService.add({
          severity: 'warn',
          summary: this.translate.instant('layout.configurations.users.assignRolesToUser.roles.noChanges'),
          detail: this.translate.instant('layout.configurations.users.assignRolesToUser.roles.noUpdateMessage') 
        });
      } else {
        // Update role in shared service
        this.sharedRoleService.updateRole(updatedRole);
        this.getAllRoles();
        this.messageService.add({
          severity: 'success',
          summary: this.translations['layout.configurations.roles.editRole.successMessage.title'],
          detail: this.translations['layout.configurations.roles.editRole.successMessage.detail'],
        });
      }}
    });
  }

  onDelete(event: Event, id: number) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: this.translations['layout.configurations.roles.deleteRole.confirmationMessage'],
      header: this.translate.instant('common.confirmation'),
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'none',
      rejectIcon: 'none',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => {
        this.deleteRole(id);
      },
    });
  }

  deleteRole(id: number) {
    this.roleService.deleteRole(id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translations['layout.configurations.roles.deleteRole.successMessage.title'],
          detail: this.translations['layout.configurations.roles.deleteRole.successMessage.detail'],
        });
        this.getAllRoles();
      },
      error: () => {
        // Remove from mock data as fallback
        this.roles = this.roles.filter(role => role.id !== id);
        this.messageService.add({
          severity: 'success',
          summary: this.translations['layout.configurations.roles.deleteRole.successMessage.title'],
          detail: this.translations['layout.configurations.roles.deleteRole.successMessage.detail'],
        });
      }
    });
  }


}
